import React, { PureComponent, MouseEvent } from "react";
import {connect, DispatchProp} from "react-redux";
import { cssHelper } from "../../shared/utils/cssHelper";
import formStyles from "../../shared/styles/forms.module.css";
import dialogStyles from "../../shared/components/dialog-manager/templates/dialog.module.css";
import styles from "./profile.module.css";
import icons from "../../shared/styles/icons.module.css";
import { AnimationSettingsProp, ILagnuageSettings } from "../../store/settings/types";
import { IApplicationState } from "../../store";
import i18n from "../../shared/i18n";
import playSound from "../../shared/sounds/playSound";
import { loadScene } from "../../store/routing/actions";
import { Language } from "../../store/settings/Language";
import getFontSize from "../../shared/utils/getFontSize";
import topPanelStyles from "../../shared/components/top-panel/top-panel.module.css";
import renderText from "../../shared/components/renderText";
import interpolate from "../../shared/utils/interpolate";
import { changeProfile } from "../../store/player/actions";

export interface ProfileSceneProps extends DispatchProp, AnimationSettingsProp {
    readonly userId: number;
    readonly name: string;
    readonly language: Language;
    readonly picture: string;
    readonly avalibaleLanguages: ILagnuageSettings[];
    readonly defaultUserPictureTemplate: string;
}

interface FormDialogState {
    loading: boolean;
    name: string;
    language: Language;
    picture: string;
    avatarIndex: number;    
}

class ProfileScene extends PureComponent<ProfileSceneProps, FormDialogState> {
    protected form: React.RefObject<HTMLFormElement> = React.createRef();

    constructor(props: ProfileSceneProps, context: any) {
        super(props, context);

        this.controlChanged = this.controlChanged.bind(this);

        const avatarIndex = this.getAvatarIndex(props.picture);

        this.state = {
            loading: false,
            name: props.name,
            language: props.language,
            picture: interpolate(this.props.defaultUserPictureTemplate, { userId: avatarIndex }),
            avatarIndex: avatarIndex
        };
    }

    getAvatarIndex(picture: string) {
        const prefix = this.props.defaultUserPictureTemplate.split('{')[0];

        const avatarIndex = !picture.startsWith(prefix)
            ? this.props.userId % 244
            : parseInt(picture.substr(prefix.length));

        return Math.max(1, avatarIndex);
    }

    render() {
        const enterName = i18n("editprofiledialog.enteryourname");
        const selectLanguage = i18n("editprofiledialog.selectlanguage");

        const fontSize = Math.min(
            getFontSize(enterName.length, 28, 36, 20, 36),
            getFontSize(selectLanguage.length, 28, 36, 20, 36),
        );

        return (
            <div className={styles.container}>
                <div className={topPanelStyles.top_panel}>
                    <button className={cssHelper(topPanelStyles.back_button, icons.back).toString()}
                            onClick={() => this.back()}>Back
                    </button>

                    {renderText(i18n("editprofiledialog.header"), 32, 58, 15, 25, styles.header)}
                </div>

                <form ref={this.form} className={styles.form_container} onSubmit={(event) => event.preventDefault()}>
                    <div className={cssHelper(formStyles.control_group, styles.avatar_chooser).toString()}>
                        <button className={styles.switch_left} onClick={e => this.nextAvatar(e, -1)} disabled={this.state.avatarIndex <= 1}>&lt;</button>
                        <div className={styles.avatar} style={{ backgroundImage: `url(${this.state.picture})` }}></div> 
                        <button className={styles.switch_right} onClick={e => this.nextAvatar(e, 1)} disabled={this.state.avatarIndex >= 243}>&gt;</button>
                    </div>

                    <div className={formStyles.control_group}>
                        <label htmlFor="profile_name" className={formStyles.control_label} style={{fontSize}}>{enterName}</label>
                        <input id="profile_name" name={"name"} type="text" defaultValue={this.state.name} className={formStyles.control} onChange={this.controlChanged} required/>
                    </div>
                
                    <div className={formStyles.control_group}>
                        <label htmlFor="profile_language" className={formStyles.control_label} style={{fontSize}}>{selectLanguage}</label>
                        <select id="profile_language" name={"language"} defaultValue={this.state.language} className={formStyles.control} onChange={this.controlChanged} required>
                            {this.renderLanguageOptions()}
                        </select>
                    </div>

                    <div className={styles.actions}>
                        <button className={dialogStyles.button} onClick={() => this.submit()} disabled={this.state.loading}>
                            {i18n("editprofiledialog.submitbutton")}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    componentDidMount(): void {
        if (!this.form.current)
            return;

        const field = this.form.current.querySelector(`input, textarea`) as HTMLInputElement;
        if (!field)
            return;

        setTimeout(() => {
            field.focus();
            field.selectionStart = field.selectionEnd = field.value.length;
        }, 300);
    }

    private renderLanguageOptions() {
        return this.props.avalibaleLanguages.map(language => (
            <option key={language.value} value={language.value}>{language.title}</option>
        ));
    }

    private nextAvatar(e: MouseEvent<HTMLButtonElement>, direction: number) {
        const avatarIndex = Math.max(1, this.state.avatarIndex + direction % 244);
        this.setState({
            avatarIndex: avatarIndex,
            picture: interpolate(this.props.defaultUserPictureTemplate, { userId: avatarIndex }),
        });

        e.preventDefault();
        e.stopPropagation();
    }

    private submit() {
        this.setState({
            loading: true
        });

        this.props.dispatch(changeProfile.request({
            name: this.state.name,
            picture: this.state.picture,
            language: this.state.language,
            locale: this.props.avalibaleLanguages?.find(lang => lang.value === this.state.language)?.locale ?? 'en-US'
        }));
    }

    private back() {
        playSound("button-click");
        this.props.dispatch(loadScene.request('MainMenu'));
    }

    private controlChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        this.setState({
            ...this.state,
            [event.target.name]: event.target.value
        });
    }
};

export default connect(({ settings, player }: IApplicationState) => ({
    userId: settings.userId || 0,
    name: player.name || "",
    picture: player.picture || "",
    language: settings.general.uiLanguage,
    animation: settings.animation,
    avalibaleLanguages: settings.general.availableLanguages,
    defaultUserPictureTemplate: settings.general.defaultUserPictureTemplate
}))(ProfileScene);